import { cloneElement, type ReactElement, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { InfoIcon, MessageSquareIcon, MessageSquarePlusIcon, SparklesIcon } from 'lucide-react';
import {
  Badge,
  Button,
  Separator,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@monksflow/monks-ui';
import { PROJECT_PERMISSIONS } from '@inference.engine/permissions/web';
import { ScopedPermission } from '@/shared/ui';
import { registerBriefSectionElement } from '@/features/comment-on-file';

interface ShellSection {
  name: string;
  required: boolean;
  retired: boolean;
}

interface ControlProps {
  disabled?: boolean;
}

/**
 * The seven per-type helper-text keys (namespace `projects`). A literal union, not `string` —
 * `t()` is compile-time checked against the real key set, so a typo here fails `tsc` the same
 * way an inline literal would.
 */
export type BriefSectionHelperTextKey =
  | 'projectBriefDetail.briefSectionShell.text.helperText'
  | 'projectBriefDetail.briefSectionShell.date.helperText'
  | 'projectBriefDetail.briefSectionShell.select.helperText'
  | 'projectBriefDetail.briefSectionShell.checklist.helperText'
  | 'projectBriefDetail.briefSectionShell.url.helperText'
  | 'projectBriefDetail.briefSectionShell.file.helperText'
  | 'projectBriefDetail.briefSectionShell.boolean.helperText';

/**
 * Present only while the value in this section is one an agent wrote and the briefer has not
 * yet stood behind it. Editing the field is the third way out and needs nothing here: the
 * control stays live, and the caller drops the marker when a change comes through it.
 */
export interface AiSuggestionActions {
  onAccept: () => void;
  onClear: () => void;
}

interface BriefSectionShellProps {
  section: ShellSection;
  helperTextKey: BriefSectionHelperTextKey;
  onAddCommentInitiated?: () => void;
  aiSuggestion?: AiSuggestionActions;
  /**
   * How many comments are anchored to this section, of either kind. Above zero the slot gains
   * an always-visible count beside the hover-revealed add affordance; the two coexist rather
   * than replacing one another.
   */
  commentCount?: number;
  /** Opens this section's thread in the rail. */
  onOpenComments?: () => void;
  /**
   * Registers this section's element so a comment selected in the rail can scroll to it. Every
   * section registers, not just the prose ones, because a whole-section comment can target a
   * widget that never mounts an editor.
   */
  sectionId?: string;
  /** The control — a widget or `BriefSectionEditor` — must accept a `disabled` prop. */
  children: ReactElement<ControlProps>;
  /**
   * Rendered directly under the control, inside the section box. It is a separate slot rather
   * than a second child because `children` is the ONE control the retired path clones
   * `disabled` onto; anything else passed there would take that clone instead of the widget.
   */
  belowControl?: ReactNode;
}

/**
 * Non-type-specific section chrome shared by EVERY section, prose and widget alike: the small
 * uppercase label, the required asterisk, an info tooltip carrying the type's helper text, the
 * control, a separator, and a "Comment on section" affordance that waits for hover or focus.
 * Read-only rendering and the retired badge live here so every widget stays a pure, controlled
 * component.
 *
 * Measured against `.local/prototypes/mf-brief-improvements-150926.html`: label 11px/16px at
 * 0.06em uppercase in `foreground-subtle`, 28px label row, 12px label-to-control, 24px
 * control-to-separator, a 36px text inset and a 12px horizontal bleed.
 */
export function BriefSectionShell({
  section,
  helperTextKey,
  onAddCommentInitiated,
  aiSuggestion,
  commentCount = 0,
  onOpenComments,
  sectionId,
  children,
  belowControl,
}: BriefSectionShellProps) {
  const { t } = useTranslation('projects');

  const control = section.retired ? (
    cloneElement(children, { disabled: true })
  ) : (
    <ScopedPermission
      permissions={[
        PROJECT_PERMISSIONS.ProjectUploadAssets,
        PROJECT_PERMISSIONS.ProjectEditDetails,
      ]}
      message={t('projectBriefDetail.briefSectionEditor.youDoNotHavePermission')}
    >
      {children}
    </ScopedPermission>
  );

  return (
    <TooltipProvider>
      {/*
       * `group` is what lets the comment affordance wait for hover or focus anywhere in the
       * section. The horizontal bleed plus the text inset reproduce the prototype's document
       * column: the section box reaches past the text on both sides, while the text itself
       * sits on the column's own left edge, clear of the floating format bar.
       */}
      <section
        ref={sectionId ? registerBriefSectionElement(sectionId) : undefined}
        data-section-id={sectionId}
        aria-label={section.name}
        className="group relative -mx-sm flex flex-col gap-xl rounded-lg p-sm"
      >
        <div className="flex flex-col gap-sm pl-6">
          <div className="flex min-h-7 items-center justify-between gap-xs">
            <div className="flex items-center gap-xs">
              <span className="text-label-xs font-medium uppercase tracking-[0.06em] text-foreground-subtle">
                {section.name}
                {section.required && (
                  <span
                    data-testid="brief-section-shell-required-asterisk"
                    aria-hidden="true"
                    className="ml-0.5 text-destructive"
                  >
                    *
                  </span>
                )}
              </span>
              {/* The asterisk is decorative, so on its own the requirement never reaches a
                  screen reader. This says it in words, visible only to assistive tech. */}
              {section.required && (
                <span data-testid="brief-section-shell-required-label" className="sr-only">
                  {t('projectBriefDetail.briefSectionShell.required')}
                </span>
              )}
              <Tooltip>
                <TooltipTrigger asChild>
                  <button
                    type="button"
                    data-testid="brief-section-shell-info-trigger"
                    aria-label={t('projectBriefDetail.briefSectionShell.helperTextTooltip')}
                    className="text-foreground-subtle"
                  >
                    <InfoIcon className="size-3.5" />
                  </button>
                </TooltipTrigger>
                <TooltipContent>{t(helperTextKey)}</TooltipContent>
              </Tooltip>
              {aiSuggestion && (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Badge
                      data-testid="brief-section-shell-ai-badge"
                      variant="outline"
                      className="ml-1 gap-1"
                    >
                      <SparklesIcon className="size-3" aria-hidden />
                      {t('projectBriefDetail.briefSectionShell.suggestedByAi')}
                    </Badge>
                  </TooltipTrigger>
                  <TooltipContent>
                    {t('projectBriefDetail.briefSectionShell.suggestedByAiExplainer')}
                  </TooltipContent>
                </Tooltip>
              )}
              {section.retired && (
                <Badge
                  data-testid="brief-section-shell-retired-badge"
                  variant="secondary"
                  className="ml-1"
                >
                  {t('projectBriefDetail.briefSectionShell.retired')}
                </Badge>
              )}
            </div>

            <div className="flex shrink-0 items-center gap-2xs">
              <Tooltip>
                <TooltipTrigger asChild>
                  <button
                    type="button"
                    data-testid="brief-section-shell-comment-affordance"
                    aria-label={t('projectBriefDetail.briefSectionShell.commentOnSection')}
                    className="text-foreground-subtle opacity-0 transition-opacity duration-fast ease-out hover:text-foreground group-hover:opacity-100 group-focus-within:opacity-100 focus-visible:opacity-100"
                    onClick={onAddCommentInitiated}
                  >
                    <MessageSquarePlusIcon className="size-3.5" />
                  </button>
                </TooltipTrigger>
                <TooltipContent>
                  {t('projectBriefDetail.briefSectionShell.commentOnSection')}
                </TooltipContent>
              </Tooltip>

              {/*
                Never hover-gated: an existing discussion has to be visible at rest, or the only
                way to discover it is to sweep the document with the pointer. Blue is the one
                strong colour in this surface, and it is what marks a section as discussed.
              */}
              {commentCount > 0 && (
                <button
                  type="button"
                  data-testid="brief-section-shell-comment-count"
                  aria-label={t('projectBriefDetail.briefSectionShell.commentsOnThisSection', {
                    count: commentCount,
                  })}
                  className="flex items-center gap-2xs rounded-lg px-xs py-0.5 text-blue-600 hover:bg-accent"
                  onClick={onOpenComments}
                >
                  <MessageSquareIcon className="size-3.5" />
                  <span className="text-body-sm font-medium">{commentCount}</span>
                </button>
              )}
            </div>
          </div>

          {control}

          {belowControl}

          {aiSuggestion && (
            <div className="flex items-center gap-xs">
              <Button
                data-testid="brief-section-shell-ai-accept"
                size="sm"
                onClick={aiSuggestion.onAccept}
              >
                {t('projectBriefDetail.briefSectionShell.acceptSuggestion')}
              </Button>
              <Button
                data-testid="brief-section-shell-ai-clear"
                variant="ghost"
                size="sm"
                onClick={aiSuggestion.onClear}
              >
                {t('projectBriefDetail.briefSectionShell.clearSuggestion')}
              </Button>
            </div>
          )}
        </div>

        <Separator data-testid="brief-section-shell-separator" className="bg-border-lighter" />
      </section>
    </TooltipProvider>
  );
}
